import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { useState } from "react";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../libs/core/components/data-table";
import { useDeleteAwardAreaMutation, useGetAwardAreasQuery } from "./api/awardareaApi";
import { AwardArea } from "./types/awardarea";
import AddEditAwardArea from "./components/AddEditAwardArea";
import { ConfirmModal } from "../../libs/ui/modal/confirm_modal";

const AwardAreaManager: React.FC = () => {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedAwardArea, setSelectedAwardArea] = useState<AwardArea | null>(null);
  const navigate = useNavigate();

  // Using RTK Query to fetch award areas
  const { data: awardareas, isLoading, isError, isFetching } = useGetAwardAreasQuery();
  const [deleteAwardArea] = useDeleteAwardAreaMutation();

  const columns: Array<MRT_ColumnDef<AwardArea>> = [
    {
      header: "Name",
      accessorKey: "name",
      size: 100,
    },
    {
      header: "Description",
      accessorKey: "description",
      size: 150,
      Cell({ row: { original } }) {
        const desc = original?.description ?? "";
        const truncated = desc.length > 100
          ? desc.slice(0, 50) + '...'
          : desc;
        return <span>{truncated || <i>No description</i>}</span>;
      },
    },
  ];

  return (
    <Box>
      <DataTable<AwardArea>
        toolbar={
          <Box display="flex" alignItems="center" width="100%">
            <Box display="flex" gap={3} alignItems="center" flex="1">
              <Typography variant="h6">Award Areas</Typography>
              <Chip
                color="primary"
                variant="outlined"
                label={`${awardareas?.data?.length ?? 0} Award Areas`} 
              />
            </Box>
            <Button
              onClick={() => {
                setSelectedAwardArea(null);
                setOpenDialog(true);
              }}
              sx={{ minWidth: "200px" }}
              variant="contained"
            >
              Add New Award Area
            </Button>
          </Box>
        }
        list={awardareas?.data ?? []}
        count={awardareas?.data?.length ?? 0}
        columns={columns}
        getRowId={(row) => row?.id?.toString()}
        isLoading={isLoading}
        isSuccess={!isError}
        isError={isError}
        isFetching={isFetching}
        renderRowActions={(row) => (
          <Box display={"flex"} gap={1}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAwardArea(row);
                setOpenConfirmModal(true);
              }}
            >
              <Delete />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAwardArea(row);
                setOpenDialog(true);
              }}
            >
              <Edit />
            </IconButton>
          </Box>
        )}
        pagination={pagination}
        onPaginationChange={setPagination}
        onRowClick={(row) => navigate(`/awardareas/${row.id}`)}
        rowNumberMode={undefined}
      />

      {/* Add/Edit Award Area Dialog */}
      <AddEditAwardArea 
        open={openDialog} 
        awardArea={selectedAwardArea}
        setModalOpen={setOpenDialog} 
      />

      {/* Confirm Delete Modal */}
      {selectedAwardArea && (
        <ConfirmModal
          type="primary"
          title="Delete Award Area"
          message={`Are you sure you want to delete the award area '${selectedAwardArea.name}'?`}
          modalOpen={openConfirmModal}
          setModalOpen={setOpenConfirmModal}
          onOk={() => {
            deleteAwardArea(selectedAwardArea.id.toString());
            setOpenConfirmModal(false);
            setSelectedAwardArea(null);
          }}
        />
      )}
    </Box>
  );
};

export default AwardAreaManager;